import { DOCK_DEFAULT_APPS, VISIBLE_DOCK_APP_IDS } from '../components/Space/EntitySidebar/pinnedApps';

export type DockEdge = 'bottom' | 'left' | 'right';
export type DockSizePreset = 'small' | 'medium' | 'large';
export type DockVisibility = 'always' | 'hover';

export const DOCK_ICON_MIN_PX = 24;
export const DOCK_ICON_MAX_PX = 96;

/**
 * The `lg` AppSquircle has a hardcoded 46px box with a fixed-pixel clip-path,
 * so it can only be resized via `transform: scale()` — which needs a unitless
 * factor (CSS can't divide two lengths). This is the ONE value that must be
 * computed in JS; everything else in the dock derives from `--dock-icon-size`
 * in CSS. Keep this the single source of truth for the scale so the live drag
 * and the React render never disagree.
 */
export const DOCK_SQUIRCLE_BASE_PX = 46;

/** Fraction of the icon-button the squircle fills. Tuned so the default 52px
 *  size reproduces the historical look while scaling perfectly uniformly. */
export const DOCK_SQUIRCLE_FILL = 0.8;

/** Unitless transform scale for the `lg` squircle at a given icon-button size. */
export function squircleScaleForSize(iconSizePx: number): number {
  return (iconSizePx * DOCK_SQUIRCLE_FILL) / DOCK_SQUIRCLE_BASE_PX;
}

/** Icon button size in px for each size preset. */
export const DOCK_SIZE_PX: Record<DockSizePreset, number> = {
  small: 36,
  medium: 52,
  large: 60,
};

/** Return the preset name closest to a raw px value. */
export function presetForPx(px: number): DockSizePreset {
  const entries = Object.entries(DOCK_SIZE_PX) as [DockSizePreset, number][];
  let closest: DockSizePreset = 'medium';
  let minDiff = Infinity;
  for (const [preset, value] of entries) {
    const diff = Math.abs(value - px);
    if (diff < minDiff) {
      minDiff = diff;
      closest = preset;
    }
  }
  return closest;
}

export function pxForPreset(preset: DockSizePreset): number {
  return DOCK_SIZE_PX[preset];
}

export interface DockPreferences {
  apps: string[];
  edge: DockEdge;
  iconSize: number;
  visibility: DockVisibility;
}

export const DEFAULT_DOCK_CONFIG: DockPreferences = {
  apps: DOCK_DEFAULT_APPS,
  edge: 'bottom',
  iconSize: DOCK_SIZE_PX.medium,
  visibility: 'always',
};

// ── Preference sanitizing ────────────────────────────────────────────────────
// Single source of truth for turning a raw (possibly stale/malformed) profile
// value into a valid DockPreferences. Shared by `useDockConfig` (reactive,
// component-facing) and imperative geometry utilities (e.g. `getDockInsets`)
// that need the same values outside of React's render cycle.

export function sanitizeApps(apps: unknown): string[] {
  if (!Array.isArray(apps)) return DEFAULT_DOCK_CONFIG.apps;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of apps) {
    if (typeof id !== 'string') continue;
    if (!VISIBLE_DOCK_APP_IDS.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  // If nothing valid, fall back to defaults
  return out.length > 0 ? out : DEFAULT_DOCK_CONFIG.apps;
}

export function sanitizeEdge(edge: unknown): DockEdge {
  if (edge === 'bottom' || edge === 'left' || edge === 'right') return edge;
  return DEFAULT_DOCK_CONFIG.edge;
}

export function sanitizeIconSize(size: unknown): number {
  if (typeof size === 'number' && size >= DOCK_ICON_MIN_PX && size <= DOCK_ICON_MAX_PX) return size;
  return DEFAULT_DOCK_CONFIG.iconSize;
}

export function sanitizeVisibility(visibility: unknown): DockVisibility {
  if (visibility === 'always' || visibility === 'hover') return visibility;
  return DEFAULT_DOCK_CONFIG.visibility;
}

export function sanitizeDockPreferences(raw: Partial<DockPreferences> | undefined): DockPreferences {
  return {
    apps: sanitizeApps(raw?.apps),
    edge: sanitizeEdge(raw?.edge),
    iconSize: sanitizeIconSize(raw?.iconSize),
    visibility: sanitizeVisibility(raw?.visibility),
  };
}

/**
 * Read + sanitize dock preferences directly off a raw user profile object.
 * Used outside of React (e.g. by `getDockInsets` in windowUtils.ts) where a
 * hook can't be called.
 */
export function readDockPreferencesFromProfile(profile: unknown): DockPreferences {
  const dock = (profile as { skeleton?: { preferences?: { dock?: unknown } } })
    ?.skeleton?.preferences?.dock;
  return sanitizeDockPreferences(
    dock && typeof dock === 'object' ? (dock as Partial<DockPreferences>) : undefined
  );
}
